<script setup lang="ts">
/*
* Компонент CalendarYearGrid - годовой режим календаря
*
* FullCalendar не умеет раскладку из макета (12 колонок-месяцев со счётчиком
* и списком событий), поэтому режим «Год» рисуется отдельно от сетки FullCalendar.
*/

import type { TCalendarEventSource } from '@/modules/calendar/types/ICalendarEvent'
import { openCalendarEvent } from '@/modules/calendar/utils/openCalendarEvent'

export interface IYearGridEvent {
  id: string
  label: string
  title: string
  start: string
  url?: string
  source?: TCalendarEventSource
  color?: string
}

const props = defineProps<{
  events: IYearGridEvent[]
  year: number
}>()

const monthLabels = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']

/*
* Дата разбирается по строке ISO, а не через Date: конструктор трактует
* «YYYY-MM-DD» как UTC и в отрицательных часовых поясах сдвигает событие
* на предыдущий месяц.
*/
const getYear = (date: string) => Number(date.slice(0, 4))
const getMonthIndex = (date: string) => Number(date.slice(5, 7)) - 1

const months = computed(() => monthLabels.map((label, index) => ({
  label,
  events: props.events.filter(event =>
    getYear(event.start) === props.year && getMonthIndex(event.start) === index),
})))

const openEvent = (event: IYearGridEvent) => {
  openCalendarEvent({
    id: event.id,
    url: event.url,
    extendedProps: { source: event.source },
  })
}
</script>

<template>
  <div class="year-grid d-flex overflow-x-auto pa-4">
    <div
      v-for="month in months"
      :key="month.label"
      class="year-grid__column d-flex flex-column ga-3 px-3"
    >
      <div class="d-flex flex-column align-center">
        <span class="text-body-1 font-weight-medium">{{ month.label }}</span>
        <span class="text-body-1 text-medium-emphasis">{{ month.events.length }}</span>
      </div>

      <div class="d-flex flex-column ga-1">
        <VTooltip
          v-for="event in month.events"
          :key="event.id"
          location="top"
          :text="event.title"
        >
          <template #activator="{ props: tooltipProps }">
            <VChip
              v-bind="tooltipProps"
              :color="event.color"
              :link="Boolean(event.source)"
              variant="tonal"
              size="small"
              class="year-grid__chip"
              @click="openEvent(event)"
            >
              {{ event.label }}
            </VChip>
          </template>
        </VTooltip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.year-grid {
  min-block-size: 40.625rem;

  /*
  * Колонки делят ширину поровну (flex-grow), но не сжимаются ниже 8.75rem —
  * при нехватке места включается горизонтальный скролл вместо мятых колонок.
  */
  &__column {
    flex: 1 0 8.75rem;

    &:not(:last-child) {
      border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    }
  }

  &__chip {
    justify-content: flex-start;
  }
}
</style>
